import {
  ReconciliationCommissionAdjustment,
  ReconciliationCommissionBreakdown,
  ReconciliationStatus,
} from 'apis/reconciliation';
import dayjs from 'dayjs';
import { getCommissionAdjustmentLabel } from 'pages/reconciliation/utils/adjustments';

/** Formats an AED amount as a 2dp, thousands-separated string. `null` (not entered) or `undefined`
 * (an old BE that never populated this field) both render as an em dash. */
export const formatAed = (n: number | null | undefined): string => {
  if (n == null) return '—';
  return n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

/** Signed variance string — matches the final prototype exactly: `+1,234.00` when positive,
 * `−98.00` (a real U+2212 minus sign, not a hyphen) when negative, `0.00` when exactly zero,
 * `—` when the SOA hasn't been entered yet (`null`). Colors are sign-based (see `varianceColor`)
 * rather than tolerance-based — the 0.01 float-safety tolerance still exists, but only server-side
 * as the `hasDiscrepancy` business flag; display no longer treats near-zero variances specially. */
export const formatVariance = (v: number | null): string => {
  if (v === null) return '—';
  const sign = v > 0 ? '+' : v < 0 ? '−' : '';
  return `${sign}${formatAed(Math.abs(v))}`;
};

/** `null` (SOA not entered yet) is neutral gray; negative is red; zero and positive are both green
 * — a variance of exactly zero is a clean reconciliation, not a "no data" state. */
export const varianceColor = (v: number | null): string => {
  if (v === null) return 'gray.400';
  return v < 0 ? '#C53030' : '#2f9e22';
};

// Fully collected uses the blue family (Chakra blue.400 dot / blue.600 text) — a deliberate
// design choice so it reads as "money in, month still open": distinct from both the closed
// green (the only terminal state) and the partial amber.
export const STATUS_META: Record<ReconciliationStatus, { label: string; dotColor: string; textColor: string }> = {
  open: { label: 'Open', dotColor: '#CBD5E0', textColor: '#718096' },
  partially_collected: { label: 'Partially collected', dotColor: '#FF9800', textColor: '#c77c00' },
  collected: { label: 'Fully collected', dotColor: '#4299E1', textColor: '#2b6cb0' },
  closed: { label: 'Closed', dotColor: '#51E03E', textColor: '#2f9e22' },
};

/** Row/panel status label with the same contextual suffix used across the table and the
 * collection panel header — closed shows the date it was frozen, open shows whether the invoice
 * went out. Partially/fully collected have no further breakdown to append. Note: unlike an earlier
 * revision, the open-state label never appends the invoice-sent date — the final prototype only
 * ever shows "Open · invoice sent" / "Open · invoice not sent". */
export const getStatusLabel = (row: {
  status: ReconciliationStatus;
  invoiceSent: boolean;
  invoiceSentAt: string | null;
  closedAt: string | null;
}): string => {
  // `?? open` guards the BE-first deploy gap (and future status additions): an unknown status
  // value degrades to a stale "Open" label instead of a TypeError blanking the table.
  const meta = STATUS_META[row.status] ?? STATUS_META.open;
  if (row.status === 'closed') {
    return row.closedAt ? `${meta.label} · ${dayjs(row.closedAt).format('DD MMM')}` : meta.label;
  }
  if (row.status === 'open') {
    return row.invoiceSent ? `${meta.label} · invoice sent` : `${meta.label} · invoice not sent`;
  }
  return meta.label;
};

/** Tooltip for a supplier row's Revenue cell — spells out the per-order basis behind the number
 * (`Σ per-order commissions · 12 percent + 2 fixed`), plus a ` · N pending` suffix when any orders
 * are awaiting commission terms, and an ` · invoice adds N% VAT` suffix when the supplier has a VAT
 * rate (the invoice amount is revenue + VAT, so the tooltip explains why the invoice exceeds the
 * shown revenue). No VAT suffix when vatRate is null or 0. When `commissionBreakdown` is null (a
 * month frozen under the old monthly-rate model, before per-order commissions existed) the base
 * string says so instead of asserting a per-order basis those months never had.
 * Kept a plain string on purpose so it can sit directly in a Chakra Tooltip `label`. */
export const getRevenueTooltip = (row: {
  commissionBreakdown: ReconciliationCommissionBreakdown | null;
  vatRate: number | null;
  commissionAdjustment?: ReconciliationCommissionAdjustment | null;
}): string => {
  const bd = row.commissionBreakdown;
  const base = bd
    ? `Σ per-order commissions · ${bd.percentOrders} percent + ${bd.fixedOrders} fixed`
    : 'Frozen under the previous monthly-rate model';
  const waived = bd && (bd.waivedOrders ?? 0) > 0 ? ` · ${bd.waivedOrders} waived` : '';
  const pending = bd && bd.pendingTerms > 0 ? ` · ${bd.pendingTerms} pending` : '';
  // Month-level waiver/adjustment sits between the per-order basis and VAT — it is what turns
  // baseRevenue into the shown revenue.
  const adjustmentLabel = getCommissionAdjustmentLabel(row.commissionAdjustment ?? null);
  const adjustment = adjustmentLabel ? ` · ${adjustmentLabel}` : '';
  const vat = row.vatRate ? ` · invoice adds ${row.vatRate}% VAT` : '';
  return `${base}${waived}${pending}${adjustment}${vat}`;
};

/** Shared by the collection panel's edit-mode and view-mode footers so both read identically:
 * a green "✓ Saved just now" right after a save/close mutation succeeds (session-local, owned by
 * the caller), a muted "Last saved <date> · <name>" once that transient state has passed, or
 * "Not yet edited" when nothing has ever been saved. */
export const getSavedLabel = (
  detail: { updatedAt: string | null; updatedByName: string | null },
  justSaved: boolean
): string => {
  const who = detail.updatedByName ? ` · ${detail.updatedByName}` : '';
  if (justSaved) return `✓ Saved just now${who}`;
  if (!detail.updatedAt) return 'Not yet edited';
  return `Last saved ${dayjs(detail.updatedAt).format('DD MMM')}${who}`;
};

export const getSavedLabelColor = (justSaved: boolean): string => (justSaved ? '#2f9e22' : 'gray.400');
